import { useMemo, type ReactNode } from 'react';
import { Alert, Empty, Form, Input, Radio, Select, Space, Tag, Typography } from 'antd';
import type { AiApplicationResult } from '@/api/payouts';
import { FEE_PAYER_LABEL, SWIFT_CHARGE_LABEL } from '@/domain/payouts';

const { Text, Title } = Typography;

const REJECT_REASON: Record<'not_an_option' | 'pattern_mismatch', string> = {
  not_an_option: '不在可选项里',
  pattern_mismatch: '格式不符合要求',
};

export interface AiApplicationEditorProps {
  result: AiApplicationResult | null;
  original: ReactNode;
  manualFields: ReadonlySet<string>;
  onResultChange: (result: AiApplicationResult) => void;
  onManualField: (key: string) => void;
  showInputSources?: boolean;
  validationFields?: readonly string[];
  payeeEmptyDescription?: string;
  applicationEmptyDescription?: string;
  disabled?: boolean;
  layout?: 'three-column' | 'split-review';
}

export default function AiApplicationEditor({
  result,
  original,
  manualFields,
  onResultChange,
  onManualField,
  showInputSources = false,
  validationFields = [],
  payeeEmptyDescription = '解析后，新收款人会显示在这里',
  applicationEmptyDescription = '解析后，打款申请会显示在这里',
  disabled = false,
  layout = 'three-column',
}: AiApplicationEditorProps) {
  const missing = useMemo(() => new Set((result?.missing ?? []).map((item) => item.key)), [result]);

  function updatePayeeValue(path: string, value: string) {
    if (!result) return;
    const currency = path === 'beneficiary.bank_details.account_currency' ? value : result.currency;
    onResultChange({
      ...result,
      currency,
      payee: {
        ...result.payee,
        values: { ...result.payee.values, [path]: value },
      },
    });
    onManualField(`payee:${path}`);
  }

  function updateApplicationValue<K extends keyof AiApplicationResult['values']>(
    key: K,
    value: AiApplicationResult['values'][K],
  ) {
    if (!result) return;
    onResultChange({
      ...result,
      values: { ...result.values, [key]: value },
    });
    onManualField(`application:${key}`);
  }

  const manualHint = (key: string, searched = false) => manualFields.has(key) ? (
    <Text type="secondary" style={{ fontSize: 12 }}>
      <Tag color="blue">已人工修改</Tag>
      {searched ? '原值由 AI 搜索补齐，请按修改后的内容核对' : '将使用当前修改后的内容'}
    </Text>
  ) : undefined;

  function sourceHint(source: 'input' | 'ai_inferred' | 'ai_search' | 'default' | undefined) {
    if (source === 'ai_search') {
      return (
        <Text type="warning" style={{ fontSize: 12 }}>
          <Tag color="gold">AI 搜索补齐</Tag>不是原始数据，请重点核对
        </Text>
      );
    }
    if (source === 'ai_inferred') {
      return (
        <Text type="warning" style={{ fontSize: 12 }}>
          <Tag color="orange">AI 推断补充</Tag>不是原始数据，请重点核对
        </Text>
      );
    }
    if (source === 'input' && showInputSources) {
      return <Text type="secondary" style={{ fontSize: 12 }}><Tag color="green">来自原始数据</Tag></Text>;
    }
    if (source === 'default') {
      return <Text type="secondary" style={{ fontSize: 12 }}><Tag>系统默认</Tag></Text>;
    }
    return undefined;
  }

  const amountHelp = manualHint('application:amount')
    ?? (missing.has('amount') && !result?.values.amount?.trim() ? (
      <Text type="warning" style={{ fontSize: 12 }}>未能从原文中确定，请直接在这里补充</Text>
    ) : sourceHint(result?.sources.amount));

  const parsedPanelStyle = layout === 'three-column' ? {
    minWidth: 0,
    borderInlineStart: '1px solid var(--ant-color-border-secondary)',
    paddingInlineStart: 24,
    maxHeight: '68vh',
    overflowY: 'auto' as const,
  } : { minWidth: 0 };

  const payeePanel = (
      <section
        aria-label="SWIFT 收款人"
        style={parsedPanelStyle}
      >
        <Title level={5} style={{ marginTop: 0 }}>2. SWIFT 收款人</Title>
        {!result ? (
          <Empty description={payeeEmptyDescription} style={{ paddingTop: 110 }} />
        ) : (
          <>
            {result.payee.searchStatus === 'failed' && (
              <Alert
                type="warning"
                showIcon
                title="公开地址自动搜索暂时失败"
                description="已经识别出的内容仍可使用；缺少的收款人字段可以直接在本列补充，也可以修改原始内容后重新解析。"
                style={{ marginBottom: 12 }}
              />
            )}
            <Form layout="vertical" component="div">
              {result.payee.fields.map((field) => {
                const value = result.payee.values[field.path] ?? '';
                const source = result.payee.sources[field.path];
                const validationFailed = validationFields.includes(field.path);
                const isMissing = field.required && !value.trim();
                const manualKey = `payee:${field.path}`;
                const isFixedSwift = field.path === 'transfer_method';
                const help = manualHint(manualKey, source === 'ai_search')
                  ?? sourceHint(source)
                  ?? (isMissing ? (
                    <Text type="warning" style={{ fontSize: 12 }}>未能确定，请直接在这里补充</Text>
                  ) : field.description ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>{field.description}</Text>
                  ) : undefined);
                return (
                  <Form.Item
                    key={field.path}
                    label={field.label}
                    required={field.required}
                    validateStatus={validationFailed ? 'error' : isMissing ? 'warning' : undefined}
                    help={validationFailed ? 'Airwallex 校验未通过，请检查此字段' : help}
                  >
                    {field.options.length > 0 ? (
                      <Select
                        aria-label={field.label}
                        showSearch
                        value={value || undefined}
                        disabled={disabled || isFixedSwift}
                        placeholder={field.example || '请选择'}
                        optionFilterProp="label"
                        options={field.options.map((item) => ({
                          value: item.value,
                          label: `${item.label} (${item.value})`,
                        }))}
                        onChange={(next) => updatePayeeValue(field.path, next)}
                      />
                    ) : (
                      <Input
                        aria-label={field.label}
                        value={value}
                        disabled={disabled || isFixedSwift}
                        placeholder={field.example || '未识别'}
                        onChange={(event) => updatePayeeValue(field.path, event.target.value)}
                      />
                    )}
                  </Form.Item>
                );
              })}
            </Form>

            {result.payee.rejected.length > 0 && (
              <Space orientation="vertical" size={4} style={{ width: '100%', marginBottom: 12 }}>
                <Text strong>未采用的收款人信息</Text>
                {result.payee.rejected.map((field) => (
                  <Alert
                    key={`${field.path}-${field.value}`}
                    type="warning"
                    title={`${field.label}：${field.value}`}
                    description={REJECT_REASON[field.reason]}
                  />
                ))}
              </Space>
            )}
          </>
        )}
      </section>
  );

  const applicationPanel = (
      <section
        aria-label="打款申请"
        style={parsedPanelStyle}
      >
        <Title level={5} style={{ marginTop: 0 }}>3. 打款申请</Title>
        {!result ? (
          <Empty description={applicationEmptyDescription} style={{ paddingTop: 110 }} />
        ) : (
          <>
            {result.warnings.map((warning) => (
              <Alert key={warning} type="warning" showIcon title={warning} style={{ marginBottom: 12 }} />
            ))}
            <Form layout="vertical" component="div">
              <Form.Item label="币种" extra="币种跟随新收款人的银行账户币种；请在 SWIFT 收款人区域修改。">
                <Input aria-label="币种" readOnly value={result.currency ?? ''} placeholder="—" />
              </Form.Item>
              <Form.Item
                label="金额"
                required
                validateStatus={missing.has('amount') && !result.values.amount?.trim() ? 'warning' : undefined}
                help={amountHelp}
              >
                <Input
                  aria-label="金额"
                  disabled={disabled}
                  inputMode="decimal"
                  value={result.values.amount ?? ''}
                  addonAfter={result.currency ?? '—'}
                  onChange={(event) => updateApplicationValue('amount', event.target.value)}
                />
              </Form.Item>
              <Form.Item label="银行附言" required help={manualHint('application:reference')}>
                <Input
                  aria-label="银行附言"
                  disabled={disabled}
                  value={result.values.reference ?? ''}
                  onChange={(event) => updateApplicationValue('reference', event.target.value)}
                />
                {result.sources.reference === 'default' && !manualFields.has('application:reference') && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                    <Tag>系统默认</Tag>固定为 KOL Marketing，AI 不会覆盖
                  </Text>
                )}
              </Form.Item>
              <Form.Item label="内部备注" help={manualHint('application:note') ?? sourceHint(result.sources.note)}>
                <Input.TextArea
                  aria-label="内部备注"
                  disabled={disabled}
                  rows={3}
                  value={result.values.note ?? ''}
                  onChange={(event) => updateApplicationValue('note', event.target.value)}
                />
              </Form.Item>
              <Form.Item label="Airwallex 手续费" help={manualHint('application:feePaidBy')}>
                <Radio.Group
                  disabled={disabled}
                  value={result.values.feePaidBy}
                  options={(['PAYER', 'BENEFICIARY'] as const).map((value) => ({
                    value,
                    label: FEE_PAYER_LABEL[value],
                  }))}
                  optionType="button"
                  onChange={(event) => updateApplicationValue('feePaidBy', event.target.value)}
                />
                {result.sources.feePaidBy === 'default' && !manualFields.has('application:feePaidBy') && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                    <Tag>系统默认</Tag>原文未说明，沿用“我们承担”
                  </Text>
                )}
              </Form.Item>
              {result.values.swiftChargeOption && (
                <Form.Item label="SWIFT 中转行费用" help={manualHint('application:swiftChargeOption')}>
                  <Radio.Group
                    disabled={disabled}
                    value={result.values.swiftChargeOption}
                    options={(['PAYER', 'SHARED'] as const).map((value) => ({
                      value,
                      label: SWIFT_CHARGE_LABEL[value],
                    }))}
                    optionType="button"
                    onChange={(event) => updateApplicationValue('swiftChargeOption', event.target.value)}
                  />
                  {result.sources.swiftChargeOption === 'default' && !manualFields.has('application:swiftChargeOption') && (
                    <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                      <Tag>系统默认</Tag>原文未说明，沿用“我们承担”
                    </Text>
                  )}
                </Form.Item>
              )}
            </Form>
          </>
        )}
      </section>
  );

  if (layout === 'split-review') {
    return (
      <div
        data-testid="ai-application-split-layout"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(300px, 2fr) minmax(480px, 3fr)',
          gap: 20,
          minWidth: 820,
          alignItems: 'start',
        }}
      >
        <section
          aria-label="原始输入"
          style={{ minWidth: 0 }}
        >
          {original}
        </section>
        <div
          data-testid="ai-application-review-panel"
          style={{
            minWidth: 0,
            borderInlineStart: '1px solid var(--ant-color-border-secondary)',
            paddingInlineStart: 20,
          }}
        >
          {payeePanel}
          <div
            style={{
              borderTop: '1px solid var(--ant-color-border-secondary)',
              marginTop: 24,
              paddingTop: 24,
            }}
          >
            {applicationPanel}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="ai-application-three-column-layout"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))',
        gap: 24,
        minHeight: 500,
      }}
    >
      <section aria-label="原始输入" style={{ minWidth: 0 }}>{original}</section>
      {payeePanel}
      {applicationPanel}
    </div>
  );
}
